import React from 'react';
import { Formik, Form } from 'formik';
import { Autocomplete, TextField, Box, Chip } from '@mui/material';
import Button from 'components/shared/Button';
import { USER_ROLE } from 'features/user/userTypes';
import styles from './EmailUserRoleInvitationsForm.module.scss';

interface EmailUserRoleInvitationsFormProps {
  closeInvitationForm: () => void;
  onSubmit: (
    invitations: { email: string; role: keyof typeof USER_ROLE }[]
  ) => void;
}

const isValidEmail = (email: string) =>
  /^\S+@\S+\.\S+$/.test(email.trim());

const autocompleteSx = {
  width: '100%',
  mb: 1,
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'wrap',
    padding: '4px 8px',
  },
  '& .MuiAutocomplete-input': {
    flex: '1 1 100%',
    mt: '4px',
  },
};

const chipBaseSx = {
  mr: 1,
  mb: 1,
};

export const EmailUserRoleInvitationsForm: React.FC<EmailUserRoleInvitationsFormProps> = ({
  closeInvitationForm,
  onSubmit,
}) => {
  return (
    <Formik
      initialValues={{ emails: [] as string[], inputValue: '' }}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        const payload = values.emails
          .filter(isValidEmail)
          .map((email) => ({ email, role: USER_ROLE.User }));
        onSubmit(payload);
        resetForm();
        setSubmitting(false);
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => {
        const invalidEmails = values.emails.filter((e) => !isValidEmail(e));

        const commitInputValue = () => {
          const trimmed = values.inputValue.trim();
          if (
            trimmed &&
            isValidEmail(trimmed) &&
            !values.emails.includes(trimmed)
          ) {
            setFieldValue('emails', [...values.emails, trimmed]);
          }
          setFieldValue('inputValue', '');
        };

        return (
          <Form className={styles.wrapper}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={values.emails}
              onChange={(_, newVals) => setFieldValue('emails', newVals)}
              filterSelectedOptions
              inputValue={values.inputValue}
              onInputChange={(_, newInput) =>
                setFieldValue('inputValue', newInput)
              }
              renderTags={(tags, getTagProps) => (
                <Box className={styles.flexWrap}>
                  {tags.map((email, index) => {
                    const { key: _autoKey, ...rest } = getTagProps({ index });
                    const invalid = !isValidEmail(email);
                    return (
                      <Chip
                        key={`${email}-${index}`}
                        label={email}
                        {...rest}
                        variant={invalid ? 'outlined' : 'filled'}
                        color={invalid ? 'error' : 'default'}
                        sx={{
                          ...chipBaseSx,
                          ...(invalid && {
                            borderColor: '#D80027',
                            backgroundColor: '#FFEBEB',
                          }),
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Invite emails"
                  placeholder="Type email and press Enter"
                  onBlur={(e) => {
                    params.inputProps.onBlur?.(
                      e as React.FocusEvent<HTMLInputElement>
                    );
                    commitInputValue();
                  }}
                />
              )}
              sx={autocompleteSx}
            />

            {invalidEmails.length > 0 && (
              <Box className={styles.flexEnd}>
                <Button
                  type="button"
                  onClick={() =>
                    setFieldValue(
                      'emails',
                      values.emails.filter(isValidEmail)
                    )
                  }
                  className={`${styles.fontRed} ${styles.fontSizeS}`}
                >
                  Clear invalid ({invalidEmails.length})
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                type="button"
                onClick={() => {
                  setFieldValue('emails', []);
                  closeInvitationForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || values.emails.length === 0}
                preloader={isSubmitting}
              >
                Send invitation{values.emails.length > 1 ? 's' : ''}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmailUserRoleInvitationsForm;
